import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastContainer from '../components/ui/Toast';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(toast => {
    if (!toast || (!toast.message && !toast.title)) {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') {
        console.warn('Toast must have at least a message or title');
      }
      return null;
    }

    const id = Math.random().toString(36).substring(2, 9);
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      message: '',
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toast = useCallback(
    (message, options = {}) => {
      return addToast({ message, ...options });
    },
    [addToast]
  );

  const success = useCallback(
    (message, title) => {
      return addToast({ type: 'success', message, title });
    },
    [addToast]
  );

  const error = useCallback(
    (message, title) => {
      return addToast({ type: 'error', message, title });
    },
    [addToast]
  );

  const warning = useCallback(
    (message, title) => {
      return addToast({ type: 'warning', message, title });
    },
    [addToast]
  );

  const info = useCallback(
    (message, title) => {
      return addToast({ type: 'info', message, title });
    },
    [addToast]
  );

  const value = {
    toast,
    success,
    error,
    warning,
    info,
    addToast,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};
